import type { Operation, ResponseBody } from '@platform/openapi';
import { pickName } from './categories.ts';

export type OptionList = ResponseBody<Operation<'/admin/options', 'get'>>;
export type CatalogOption = OptionList['items'][number];
export type OptionValue = CatalogOption['values'][number];

export const MAX_OPTIONS = 3;

export const combinationKey = (valueIds: readonly string[]): string => [...valueIds].sort().join('|');

export type OptionIndex = {
  readonly valueName: (valueId: string) => string;
  readonly valueCode: (valueId: string) => string;
  readonly optionOf: (valueId: string) => CatalogOption | undefined;
};

export const indexOptions = (options: readonly CatalogOption[], locale: string): OptionIndex => {
  const values = new Map(
    options.flatMap((option) => option.values.map((value) => [value.id, { option, value }] as const)),
  );
  return {
    valueName: (valueId) => {
      const entry = values.get(valueId);
      return entry === undefined ? valueId : pickName(entry.value.names, locale, entry.value.code);
    },
    valueCode: (valueId) => values.get(valueId)?.value.code ?? valueId,
    optionOf: (valueId) => values.get(valueId)?.option,
  };
};

export type OptionValueChoice = { readonly value: string; readonly label: string };

export const optionValueChoices = (
  options: readonly CatalogOption[],
  locale: string,
): readonly OptionValueChoice[] =>
  options.flatMap((option) =>
    option.values.map((value) => ({
      value: value.id,
      label: `${pickName(option.names, locale, option.code)} · ${pickName(value.names, locale, value.code)}`,
    })),
  );

export const variantLabel = (valueIds: readonly string[], index: OptionIndex, fallback: string): string =>
  valueIds.length === 0
    ? fallback
    : [...valueIds]
        .sort((left, right) => (index.optionOf(left)?.position ?? 0) - (index.optionOf(right)?.position ?? 0))
        .map(index.valueName)
        .join(' / ');
